import * as yup from 'yup';
import {
  checkSocialName,
  hasPhoneNumbersInvalid,
  testDateIsValid,
  testFormatDate,
  testIsEqualOrGreat18Years,
  testIsGreatHundredAge,
  validatedCPF
} from '../utils/GenericFunctions';
import { GENRE, UNDER_AGE_ERROR_MESSAGE } from './Constants';
import { isEmailDomainPossiblyMisspelled } from '../utils/string';

export const schemaEmailRequired = yup.string().when('provider', (provider) => {
  if (provider?.[0]) return yup.string().email('Formato de e-mail inválido');
  else
    return yup
      .string()
      .required('Campo obrigatório')
      .email('Formato de e-mail inválido')

      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
        {
          message: 'Formato de e-mail inválido'
        }
      )
      .test(
        'email-domain-spellcheck',
        'Este e-mail não parece ser válido.',
        (value) => {
          if (!value) return true;
          return !isEmailDomainPossiblyMisspelled(value);
        }
      );
});

export const schemaCheckEmailRequired = yup
  .string()
  .when('isSocial', {
    is: false,
    then: (schema) => schema.required('Campo obrigatório')
  })
  .test('check-email', 'os e-mails não são iguais', (value, context) => {
    if (context.parent.email == value) {
      return true;
    }

    return false;
  });

export const schemaStringCpfRequired = yup
  .string()
  .nullable()
  .test('cpf-valido', 'Formato do CPF inválido', (value) => {
    if (!value) return true;
    return (
      /^[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}-?[0-9]{2}$/.test(value) &&
      validatedCPF(value)
    );
  });

export const schemaPasswordRequired = yup.string().when('isSocial', {
  is: false,
  then: (schema) =>
    schema
      .required('Campo obrigatório')
      .test(
        'min-length',
        'A senha deve conter ao menos 8 caracteres',
        (value) => !!value && value.length >= 8
      )
      .test(
        'symbol',
        'A senha deve conter ao menos 1 símbolo',
        (value) => !!value && /[^A-Za-z0-9]/.test(value)
      )
      .test(
        'number',
        'A senha deve conter ao menos 1 número',
        (value) => !!value && /\d/.test(value)
      )
      .test(
        'uppercase',
        'A senha deve conter ao menos 1 letra maiúscula',
        (value) => !!value && /[A-Z]/.test(value)
      ),
  otherwise: (schema) => schema.notRequired()
});

export const schemaCheckPasswordRequired = yup
  .string()
  .when('isSocial', {
    is: false,
    then: (schema) => schema.required('Campo obrigatório')
  })
  .test('check-password', 'as senhas não são iguais', (value, context) => {
    if (context.parent.password == value || context.parent.isSocial) {
      return true;
    }

    return false;
  });
export const schemaTermsRequired = yup.bool().when('isSocial', {
  is: false,
  then: (schema) => schema.required('Campo obrigatório')
});
export const schemaStringRequired = yup.string().nullable();
export const schemaStringPhoneRequired = yup.lazy((value) =>
  !value
    ? yup.string()
    : yup
        .string()
        .required('Campo obrigatório')
        .matches(/(\(\d{2}\))\s(\d{5})-(\d{4})/, 'número inválido')
        .test('number-invalid', (value, context) => {
          if (
            value &&
            typeof value == 'string' &&
            hasPhoneNumbersInvalid(value)
          ) {
            return context.createError({
              message: 'número inválido'
            });
          }
          return true;
        })
);

export const schemaCepRequired = yup
  .string()
  .nullable()
  .matches(/[0-9]{5}-[0-9]{3}/g, 'cep inválido');
export const schemaStringDateRequired = yup
  .string()
  .required('Campo obrigatório')
  .test('valid-date', (value, context) => {
    if (typeof value == 'string' && testFormatDate(value)) {
      if (testDateIsValid(value)) {
        if (!testIsEqualOrGreat18Years(value)) {
          return context.createError({
            message: UNDER_AGE_ERROR_MESSAGE
          });
        }
        if (testIsGreatHundredAge(value)) {
          return context.createError({ message: 'data inválida' });
        }
        return true;
      }
      return context.createError({ message: 'data inválida' });
    }

    return context.createError({ message: 'data inválida' });
  });
export const schemaBoolRequired = yup.bool().required('Campo obrigatório');
export const schemaBoolRequiredSocialName = yup
  .bool()
  .test('useNameSocial', (value, context) => {
    if (context.parent?.socialName?.length < 1 && value) {
      return context.createError({
        path: 'socialName',
        message: 'Campo obrigatório'
      });
    }

    return true;
  });
export const schemaNumberGenrer = yup
  .string()
  .test('gender', (value, context) => {
    if (context.parent?.gender?.length < 1 && value == GENRE.OTHER) {
      return context.createError({
        path: 'gender',
        message: 'Campo obrigatório'
      });
    }

    return true;
  });

export const schemaNoNumber = yup
  .string()
  .test('check-hasNumber', 'Campo obrigatório', (value, context) => {
    if (!value?.length && context.parent.hasAddressNumber) {
      return false;
    }
    return true;
  });

export const schemaFirstName = yup
  .string()
  .required('Campo obrigatório')
  .min(2, 'Digite ao menos 2 letras')
  .test('no-double-space', 'Nome inválido', (value) => {
    return value ? value.trim().indexOf('  ') === -1 : false;
  });

export const schemaLastName = yup
  .string()
  .required('Campo obrigatório')
  .min(2, 'Digite ao menos 2 letras')
  .test('no-double-space', 'Sobrenome inválido', (value) => {
    return value ? value.trim().indexOf('  ') === -1 : false;
  });

export const schemaFullName = yup
  .string()
  .required('Campo obrigatório')
  .test('check-full-name', 'digite seu nome e sobrenome', (value) => {
    value = value?.trim();
    if (
      value &&
      value.trim().split(' ')[0] &&
      value.trim().split(' ')[0]?.length > 1 &&
      value.trim().split(' ')[1] &&
      value.trim().split(' ')[1]?.length > 1 &&
      value?.indexOf('  ') === -1
    ) {
      return true;
    }
    return false;
  });

export const schemaSocialName = yup
  .string()
  .test(
    'check-social-name',
    'digite seu nome e sobrenome',
    (value, context) => {
      if (context.parent?.hasSocialName)
        if (!value || checkSocialName(value)) return false;

      return true;
    }
  );
